import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  Injector,
  Output,
  EventEmitter,
  TemplateRef,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { ConnectedPosition, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { LookupContract } from '../cross-cutting/models';
import { OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import { BehaviorSubject, Observable, debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { TsGroupByPipe } from '../cross-cutting/pipes';
import { randomId } from '../cross-cutting/util';

export type TsSelectOptionMetadata = {
  disabled?: boolean;
};

@Component({
  selector: 'ts-select',
  templateUrl: './ts-select.component.html',
  styleUrls: ['./ts-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: TsSelectComponent,
    },
  ],
})
export class TsSelectComponent extends OnDestroyMixin implements OnInit, ControlValueAccessor {
  @Input() label: string;
  @Input() set items(value: (any & TsSelectOptionMetadata)[]) {
    this._items = value;
    if (this.isPanelOpen.value) {
      this.updateFilteredItems(this.searchFormControl.value || '');
    }
  }
  get items(): (any & TsSelectOptionMetadata)[] {
    return this._items;
  }
  private _items: (any & TsSelectOptionMetadata)[];

  @Input() set disabled(value: boolean) {
    this.isDisabled = value;
  }
  @Input() multi = false;
  @Input() clearable = true;
  @Input() multiDisplayMode: 'compact' | 'comma' = 'compact';
  // Added this to avoid timing conflicts with document handlers in some cases.
  @Input() useMousedownSelection = false;
  @Input() hint: string;

  @Input() tooltip: string;
  @Input() listItemTooltip = false;
  @Input() lookupContract: LookupContract;
  @Input() itemKey;
  @Input() itemLabel;
  @Input() hasGroups = false;
  @Input() uiOptions: {
    roundedBorders?: boolean;
    placeholder?: string;
    searchPlaceholder?: string;
    size?: string;
    disabledClass?: string | null;
  } = {
    roundedBorders: true,
    placeholder: 'select',
    searchPlaceholder: 'Search...',
    size: 'medium',
    disabledClass: null,
  };
  @Input() selectedItemTemplate: TemplateRef<any> | null = null;
  @Input() optionDataTestIdKey: string | null = null;
  @Input() searchLoading = false;

  @Output() searchChange = new EventEmitter<string>();
  @Output() selectionCleared = new EventEmitter<void>();
  @Output() panelToggled = new EventEmitter<boolean>();

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild(CdkOverlayOrigin) overlayOrigin!: CdkOverlayOrigin;
  @ViewChild(CdkVirtualScrollViewport) virtualScroll!: CdkVirtualScrollViewport;

  selectedItem: any;
  selectedItems: any[] = [];
  searchFormControl = new FormControl('');
  searchValue$: Observable<string>;
  isPanelOpen = new BehaviorSubject(false);
  triggerWidth = 0;
  highlightedIndex = -1;
  filteredItems: any[] = [];
  ui = {
    splashId: '',
  };

  readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 2 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -2 },
  ];

  private isDisabled = false;

  get disabled() {
    const control = this.injector.get(NgControl);

    return this.isDisabled || control.disabled;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private injector: Injector,
    private elementRef: ElementRef,
  ) {
    super();

    this.ui = {
      splashId: `x${randomId()}`,
    };

    this.filterFn.bind(this);
  }

  ngOnInit(): void {
    this.searchValue$ = this.searchFormControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap((value) => {
        this.highlightedIndex = -1;
        this.updateFilteredItems(value);
        this.searchChange.emit(value ?? '');
      }),
    );
  }

  writeValue(obj: any): void {
    if (this.multi) {
      this.selectedItems = Array.isArray(obj) ? obj : [];
    } else {
      this.selectedItem = obj;
    }
    this.cdr.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  onOptionSelect(event: Event, option: any): void {
    if (option.disabled) return;

    if (option.isGroup) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (option.isCreateNewOption) {
      this.onChange(option[this.itemKey]);
      this.closeOptionsPanel();
      return;
    }

    if (this.multi) {
      this.handleMultipleSelection(option);
    } else {
      this.handleSingleSelection(option);
    }
  }

  private handleMultipleSelection(option: any) {
    // Multi-select logic
    const index = this.selectedItems.findIndex((item) => item === option[this.itemKey]);

    if (index === -1) {
      this.selectedItems = [...this.selectedItems, option[this.itemKey]]; // Trigger change detection
    } else {
      this.selectedItems = this.selectedItems.filter((item) => option[this.itemKey] !== item); // Remove from selection
    }

    this.onChange(this.selectedItems); // Emit the selected array
  }

  private handleSingleSelection(option: any) {
    if (option[this.itemKey] !== this.selectedItem) {
      this.selectedItem = option[this.itemKey];
      this.onChange(option[this.itemKey]);
    }

    this.closeOptionsPanel();
  }

  getSelectedItemInfo(): { label: string; groupLabel?: string } {
    const field = this.items?.find((x) => x[this.itemKey] === this.selectedItem);

    return {
      label: field?.[this.itemLabel],
      groupLabel: field?.meta?.group?.label,
    };
  }

  @HostListener('document:click', ['$event'])
  documentClickHandler(event: any): void {
    if (this.isDisabled) {
      return;
    }

    if (document.querySelector(`.${this.ui.splashId}`)?.contains(event.target)) {
      if (!this.isPanelOpen.value) {
        this.openOptionsPanel();
      }
    }
  }

  openOptionsPanel($event?: KeyboardEvent): void {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }

    if (this.disabled) return;

    this.triggerWidth = this.elementRef.nativeElement.getBoundingClientRect().width;
    this.isPanelOpen.next(true);
    this.panelToggled.emit(true);
  }

  onOverlayAttached(): void {
    this.highlightedIndex = -1;
    this.updateFilteredItems(this.searchFormControl.value || '');
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
      this.virtualScroll?.checkViewportSize();
    });
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveHighlight(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveHighlight(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.selectHighlighted();
    }
  }

  private moveHighlight(delta: number): void {
    if (!this.filteredItems?.length) return;

    let newIndex = this.highlightedIndex + delta;

    while (newIndex >= 0 && newIndex < this.filteredItems.length && this.filteredItems[newIndex]?.isGroup) {
      newIndex += delta;
    }

    if (newIndex < 0) newIndex = 0;
    if (newIndex >= this.filteredItems.length) newIndex = this.filteredItems.length - 1;

    if (this.filteredItems[newIndex]?.isGroup) return;

    this.highlightedIndex = newIndex;
    this.virtualScroll?.scrollToIndex(this.highlightedIndex);
    this.cdr.markForCheck();
  }

  private selectHighlighted(): void {
    if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredItems.length) {
      const item = this.filteredItems[this.highlightedIndex];
      if (!item.isGroup && !item.disabled) {
        this.onOptionSelect(new Event('keydown'), item);
      }
    }
  }

  closeOptionsPanel(): void {
    this.searchFormControl.setValue('', { emitEvent: false });
    this.isPanelOpen.next(false);
    this.panelToggled.emit(false);

    setTimeout(() => {
      this.overlayOrigin?.elementRef?.nativeElement?.focus();
    });
  }

  filterFn = (values: any, args: any): any[] => {
    if (!args) {
      return values;
    }

    if (!this.hasGroups) {
      return values.filter((x) => {
        if (x.isCreateNewOption) {
          return true;
        }
        return !x.isGroup && x[this.itemLabel]?.toLowerCase().includes(args.toLowerCase());
      });
    }

    const filtered = values.filter((x) => {
      if (x.isCreateNewOption) {
        return true;
      }
      return !x.isGroup && x[this.itemLabel].toLowerCase().includes(args.toLowerCase());
    });

    return new TsGroupByPipe().transform(filtered, true);
  };

  updateFilteredItems(searchTerm?: string): void {
    this.filteredItems = this.filterFn(this.items || [], searchTerm || '');
  }

  private onChange = (value: any) => {};

  clearSelection(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.clearable) return;

    this.selectedItems = [];
    this.onChange(this.selectedItems);
    this.selectionCleared.emit();
  }
}
